import { Button } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Check, ChevronDownIcon, Loader2 } from 'lucide-react'
import * as React from 'react'

type Option = {
	value: string
	label: string
}

interface VirtualizedCommandProps {
	height: string
	options: Option[]
	placeholder: string
	selectedOption: string
	isLoading?: boolean
	onSelectOption?: (option: string) => void
}

const VirtualizedCommand = ({
	height,
	options,
	placeholder,
	isLoading,
	selectedOption,
	onSelectOption,
}: VirtualizedCommandProps) => {
	const [filteredOptions, setFilteredOptions] = React.useState<Option[]>(options)
	const [focusedIndex, setFocusedIndex] = React.useState(0)
	const [isKeyboardNavActive, setIsKeyboardNavActive] = React.useState(false)

	const parentRef = React.useRef(null)

	const virtualizer = useVirtualizer({
		count: filteredOptions.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 35,
	})

	const virtualOptions = virtualizer.getVirtualItems()

	const scrollToIndex = (index: number) => {
		virtualizer.scrollToIndex(index, {
			align: 'center',
		})
	}

	const handleSearch = (search: string) => {
		setIsKeyboardNavActive(false)
		setFilteredOptions(
			options.filter(option => option.value.toLowerCase().includes(search.toLowerCase() ?? [])),
		)
	}

	const handleKeyDown = (event: React.KeyboardEvent) => {
		switch (event.key) {
			case 'ArrowDown': {
				event.preventDefault()
				setIsKeyboardNavActive(true)
				setFocusedIndex(prev => {
					const newIndex = prev === -1 ? 0 : Math.min(prev + 1, filteredOptions.length - 1)
					scrollToIndex(newIndex)
					return newIndex
				})
				break
			}
			case 'ArrowUp': {
				event.preventDefault()
				setIsKeyboardNavActive(true)
				setFocusedIndex(prev => {
					const newIndex = prev === -1 ? filteredOptions.length - 1 : Math.max(prev - 1, 0)
					scrollToIndex(newIndex)
					return newIndex
				})
				break
			}
			case 'Enter': {
				event.preventDefault()
				if (filteredOptions[focusedIndex]) {
					onSelectOption?.(filteredOptions[focusedIndex].value)
				}
				break
			}
			default:
				break
		}
	}

	React.useEffect(() => {
		if (selectedOption) {
			const option = filteredOptions.find(option => option.value === selectedOption)
			if (option) {
				const index = filteredOptions.indexOf(option)
				setFocusedIndex(index)
				virtualizer.scrollToIndex(index, {
					align: 'center',
				})
			}
		}
	}, [selectedOption, filteredOptions, virtualizer])

	return (
		<Command shouldFilter={false} onKeyDown={handleKeyDown}>
			<CommandInput onValueChange={handleSearch} placeholder={placeholder} />
			<CommandList
				ref={parentRef}
				style={{
					height: height,
					width: '100%',
					overflow: 'auto',
				}}
				onMouseDown={() => setIsKeyboardNavActive(false)}
				onMouseMove={() => setIsKeyboardNavActive(false)}>
				<CommandEmpty className="py-6 text-center text-sm">Keine Buchungen gefunden.</CommandEmpty>
				<CommandGroup>
					<div
						style={{
							height: `${virtualizer.getTotalSize()}px`,
							width: '100%',
							position: 'relative',
						}}>
						{isLoading ? (
							<CommandItem
								disabled={true}
								className={cn('absolute left-0 -translate-x-2 top-0 w-full bg-transparent')}
								style={{
									height: `20px`,
								}}
								onSelect={onSelectOption}>
								<Check className={cn('mr-2 h-4 w-4 opacity-0')} />
								<Loader2 className="animate-spin" />
								<p>Loading...</p>
							</CommandItem>
						) : (
							virtualOptions.map(virtualOption => (
								<CommandItem
									key={filteredOptions[virtualOption.index].value}
									disabled={isKeyboardNavActive}
									className={cn(
										'absolute left-0 top-0 w-full bg-transparent',
										focusedIndex === virtualOption.index && 'bg-accent text-accent-foreground',
										isKeyboardNavActive &&
											focusedIndex !== virtualOption.index &&
											'aria-selected:bg-transparent aria-selected:text-primary',
									)}
									style={{
										height: `${virtualOption.size}px`,
										transform: `translateY(${virtualOption.start}px)`,
									}}
									value={filteredOptions[virtualOption.index].value}
									onMouseEnter={() => !isKeyboardNavActive && setFocusedIndex(virtualOption.index)}
									onMouseLeave={() => !isKeyboardNavActive && setFocusedIndex(-1)}
									onSelect={onSelectOption}>
									<Check
										className={cn(
											'mr-2 h-4 w-4',
											selectedOption === filteredOptions[virtualOption.index].value
												? 'opacity-100'
												: 'opacity-0',
										)}
									/>
									{filteredOptions[virtualOption.index].label}
								</CommandItem>
							))
						)}
					</div>
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

interface VirtualizedComboboxProps {
	options: string[]
	searchPlaceholder?: string
	defaultOpen?: boolean
	error?: boolean
	contentClassName?: string
	isLoading?: boolean
	onChange?: (value: string) => void
	value?: string
	width?: string
	height?: string
}

export function VirtualizedCombobox({
	options,
	searchPlaceholder = 'Search items...',
	onChange,
	contentClassName,
	defaultOpen,
	error,
	isLoading,
	value,
	width = '400px',
	height = '400px',
}: VirtualizedComboboxProps) {
	const [open, setOpen] = React.useState(defaultOpen ?? false)
	const [selectedOption, setSelectedOption] = React.useState('')

	React.useEffect(() => {
		if (value === undefined) return
		setSelectedOption(value)
	}, [value])

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn(
						'hover:bg-white overflow-hidden text-ellipsis max-lg:text-xs px-4 py-[18px] h-10 lg:h-14 rounded-[12px] justify-between focus:!border-3 focus-visible:ring-[3px] text-left font-normal',
						!selectedOption && 'text-muted-foreground',
						error
							? 'border-destructive focus-visible:border-ring focus-visible:ring-destructive/50 focus:!border-destructive/50'
							: 'border-input focus-visible:border-ring focus-visible:ring-ring/50 focus:!border-secondary/50',
					)}
					style={{
						width: width,
					}}>
					{selectedOption ? options.find(option => option === selectedOption) : searchPlaceholder}
					<ChevronDownIcon className="shrink-0 size-4 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				align="start"
				className={cn('w-[200px] p-0 border-input', contentClassName)}
				style={{ width: 'var(--radix-popover-trigger-width)' }}>
				<VirtualizedCommand
					height={height}
					isLoading={isLoading}
					options={options.map(option => ({ value: option, label: option }))}
					placeholder={searchPlaceholder}
					selectedOption={selectedOption}
					onSelectOption={currentValue => {
						setSelectedOption(currentValue === selectedOption ? '' : currentValue)
						onChange?.(currentValue === selectedOption ? '' : currentValue)
						setOpen(false)
					}}
				/>
			</PopoverContent>
		</Popover>
	)
}
